import React, { RefObject, useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { durationToString, shortenedDateToString } from '../../time';
import { MIN_TO_SEC, SEC_TO_MS } from '../../cfg/const';
import { SessionStatus } from '../../components/SessionStatus/SessionStatus';
import { reloadPendingReviewsInitialState } from 'lib/redux/sessions/lists/list-slice';
import { useDispatch } from 'react-redux';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SessionDetailView, SessionHistoryTabs, viewEnforcesStart, viewSupportsInfiniteScroll, viewSupportsSessionSelect, viewSupportsCustomScores } from '../History/views';
import { CallMetricColumns, CallbackRequestColumns, PROSPECT_INFO_COLUMNS, SESSION_METRICS_COLUMNS, SessionV2Columns, ReviewStatusColumns, SessionAgentsColumns } from 'interfaces/table';
import { SessionListResponse } from 'interfaces/sessions';
import { ABSOLUTE_MAX_LOOKBACK_MONTHS, useSessionFetch } from 'lib/redux/sessions/lists/session-fetch-hook';
import { SessionListProps as SessionFetchProps } from 'lib/redux/sessions/lists/cfg';
import { 
  isSessionMissed, 
  isSessionAnswered,
  getSessionObjectionString,
  getSessionDisposition,
  getSessionSentiment,
  getSessionUserId,
  isSessionLastMessageFromAgent,
  getSessionLastMessage
} from '../../utils/session';
import { useSessionListCustomScores } from '../../lib/redux/sessions/custom-scores/session-list-custom-scores-hook';
import { CustomScoresModal } from '../CustomScores/CustomScoresModal';
import { useCustomScoresModal } from '../../hooks/useCustomScoresModal';
import { AgentType } from 'interfaces/db';

export type SessionListProps = SessionFetchProps & {
  onRowClick: (session_id: string) => void;
  shownSessionId?: string;
  selectedSessions?: string[];
  onSessionSelect?: (sessionIds: string[]) => void;
}

export function SessionList({ 
  onRowClick, shownSessionId, selectedSessions, onSessionSelect,
  ...sessionFetchProps
}: SessionListProps) {
  
  const { items: initialItems, 
    isLoading, hasMore, loadMore, 
    monthsSearched, hasStrictStartDate,
    isDebouncing
  } = useSessionFetch(sessionFetchProps)
  

  // Get custom scores for sessions
  const {
    getCustomScoresForSession
  } = useSessionListCustomScores({ sessions: initialItems })

  // Memoize items with better dependency tracking
  const items = useMemo(() => {
    // Only recalculate when tab or initial_items actually change
    switch (sessionFetchProps.tab) {
      case SessionHistoryTabs.TEXTS:
        // consolidate like texts 
        const items_sorted = [...initialItems].sort((a, b) => b.start.getTime() - a.start.getTime())
        const unique_numbers = new Set(items_sorted.map((x) => `${x.customer_address}`))
        const unique_items = items_sorted.filter((item) => {
          const key = `${item.customer_address}`;
          if (unique_numbers.has(key)) {
            unique_numbers.delete(key);
            return true;
          }
          return false;
        });
        return unique_items;
      default:
        return initialItems;
    }
  }, [initialItems, sessionFetchProps.tab]);

  const _listRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  // Track timeouts more systematically to ensure cleanup
  const timeoutsRef = useRef<{[key: string]: NodeJS.Timeout}>({});
  
  // Custom scores modal management
  const {
    isCustomScoresModalOpen,
    modalSessionId,
    modalAnchorRect,
    showModal,
    hideModal,
    cancelHideModal,
    startHideModal,
    closeModal
  } = useCustomScoresModal();
  
  
  const clearTimeout = useCallback((key: string) => {
    if (timeoutsRef.current[key]) {
      global.clearTimeout(timeoutsRef.current[key]);
      delete timeoutsRef.current[key];
    }
  }, []);
  
  const setTimeout = useCallback((callback: () => void, delay: number, key: string) => {
    // Clear existing timeout with the same key if it exists
    clearTimeout(key);
    // Set new timeout
    timeoutsRef.current[key] = global.setTimeout(callback, delay);
    return timeoutsRef.current[key];
  }, [clearTimeout]);
  
  // Clean up all timeouts on unmount
  useEffect(() => {
    // Capture the ref value at the beginning of the effect
    const timeouts = timeoutsRef.current;
    
    return () => {
      Object.keys(timeouts).forEach(key => {
        global.clearTimeout(timeouts[key]);
      });
    };
  }, []);
  
  const dispatch = useDispatch();
  
  // Get user and visible accounts from centralized hook
  const { user, visibleAccounts } = useUserData();

  // Critical: Use refs to track loading state to avoid dependency issues and stale closures
  const isLoadingRef = useRef<boolean>(isLoading);
  const hasMoreRef = useRef<boolean>(hasMore);
  const itemsRef = useRef<SessionListResponse[]>(items);
  
  // Update refs when values change to ensure current values are used in callbacks
  useEffect(() => {
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
    itemsRef.current = items;
  }, [isLoading, hasMore, items]);
  
  // Track fill attempts with a ref to avoid unwanted re-renders
  const fillAttemptsRef = useRef<number>(0);
  const MAX_FILL_ATTEMPTS = 5;

  // Group items by date - items are already sorted in the hook
  const datesToItems = useMemo(() => {
    if (!items.length) return [];
    
    // Memoize the date extraction function
    const getDateForItem = (item: SessionListResponse): Date | null => {
      if (sessionFetchProps.tab === SessionHistoryTabs.CALLBACKS) {
        const timing = item.result.get(CallbackRequestColumns.CALLBACK_TIMING);
        return timing ? timing as Date : null;
      }
      const started = item.result.get(SessionV2Columns.STARTED_AT);
      return started ? started as Date : null;
    };
    
    // Sort items by date before grouping - only if needed
    const sortedItems = [...items].sort((a, b) => {
      const dateA = getDateForItem(a);
      const dateB = getDateForItem(b);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateB.getTime() - dateA.getTime(); // Most recent first
    });
    
    // Group by date string to avoid Date object comparison issues
    const dateGroupMap = new Map<string, SessionListResponse[]>();
    
    sortedItems.forEach(item => {
      const dateToUse = getDateForItem(item);
      if (dateToUse) {
        const dateString = shortenedDateToString(dateToUse);
        if (!dateGroupMap.has(dateString)) {
          dateGroupMap.set(dateString, []);
        }
        dateGroupMap.get(dateString)!.push(item);
      }
    });
    
    // Convert to array format, preserving order
    return Array.from(dateGroupMap.entries()).map(([date, items]) => ({
      date,
      items,
    }));
  }, [items, sessionFetchProps.tab]);

  // Only flatten items once they've been grouped by date - with stable memoization
  const flattenedItems = useMemo(() => {
    return datesToItems.flatMap(dateGroup => [
      { type: 'header' as const, date: dateGroup.date },
      ...dateGroup.items.map(item => ({ type: 'item' as const, data: item }))
    ]);
  }, [datesToItems]);

  // Track virtualization readiness
  const [virtualizerReady, setVirtualizerReady] = useState(false);

  
  // Set up virtualizer with improved config and more stable references
  const rowVirtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => _listRef.current,
    estimateSize: useCallback((index) => {
      const item = flattenedItems[index];
      if (!item) return 64;
      if (item.type === 'header') return 32;
      // Increase height for callback items
      if (sessionFetchProps.tab === SessionHistoryTabs.CALLBACKS && item.type === 'item') return 74;
      return 64; // Default estimate, will be updated by measureElement
    }, [flattenedItems, sessionFetchProps.tab]),
    overscan: 5,
    getItemKey: useCallback((index: number) => {
      const item = flattenedItems[index];
      if (!item) return `item-${index}`;
      if (item.type === 'header') {
        return `header-${item.date}`;
      } else {
        const compressedState = shownSessionId ? 'compressed' : 'normal';
        return `item-${item.data.session_id || index}-${compressedState}`;
      }
    }, [flattenedItems, shownSessionId]),
    measureElement: useCallback((element: HTMLElement) => {
      // Add a small buffer for margins/padding
      return element.getBoundingClientRect().height + 6;
    }, [])
  });

  // Effect to set virtualizer as ready after initial render
  useEffect(() => {
    if (rowVirtualizer && flattenedItems.length > 0 && !virtualizerReady) {
      setTimeout(() => {
        setVirtualizerReady(true);
      }, 200, 'virtualizerReadyTimer');
      
      return () => clearTimeout('virtualizerReadyTimer');
    }
  }, [rowVirtualizer, flattenedItems.length, virtualizerReady, setTimeout, clearTimeout]);


  // Simplified scroll handler focused on checking if we need to load more - with debouncing
  const handleInfiniteScroll = useCallback(() => {
    
    // Exit early if necessary conditions aren't met
    if (!_listRef.current || isLoadingRef.current || isDebouncing || !hasMoreRef.current || !rowVirtualizer) {
      return;
    }

    if (!viewSupportsInfiniteScroll(sessionFetchProps.view)) {
      return;
    }

    const listElement = _listRef.current;
    const scrollPosition = listElement.scrollTop;
    const viewportHeight = listElement.clientHeight;
    const totalHeight = rowVirtualizer.getTotalSize();
    
    if (totalHeight <= 0 || viewportHeight <= 0) {
      return;
    }
    
    // Calculate how close to the bottom we are
    const pixelsFromBottom = Math.max(0, totalHeight - (scrollPosition + viewportHeight));
    
    // Load more if we're within 50px of the bottom (reduced from 150px)
    // This means we'll start loading later, reducing unnecessary fetches
    if (pixelsFromBottom < 50) {
      // Prevent duplicate calls by checking if we're already loading or debouncing
      if (!isLoadingRef.current && !isDebouncing) {
        loadMore(true);
      } else {
      }
    }
  }, [loadMore, rowVirtualizer, isDebouncing, sessionFetchProps.view]); // Add sessionFetchProps.view dependency
  
  // Handle infinite scroll with proper timeouts
  useEffect(() => {
    const listElement = _listRef.current;
    if (!listElement || !virtualizerReady) {
      return;
    }
    
    // Debounced scroll handler to avoid too many calls
    const debouncedScrollHandler = () => {
      clearTimeout('infiniteScrollHandler');
      setTimeout(handleInfiniteScroll, 150, 'infiniteScrollHandler');
    };
    
    listElement.addEventListener('scroll', debouncedScrollHandler, { passive: true });
    
    // Only do initial check if we have items and they should be rendered
    // Wait longer if debouncing or if we have no items to allow for rendering
    const shouldDoInitialCheck = flattenedItems.length > 0 && !isDebouncing && !isLoading;
    
    if (shouldDoInitialCheck) {
      const initialCheckDelay = 500; // Always wait for render
      setTimeout(handleInfiniteScroll, initialCheckDelay, 'initialScrollCheck');
    }
    
    return () => {
      listElement.removeEventListener('scroll', debouncedScrollHandler);
      clearTimeout('infiniteScrollHandler');
      clearTimeout('initialScrollCheck');
    };
  }, [virtualizerReady, handleInfiniteScroll, setTimeout, clearTimeout, isDebouncing, flattenedItems.length, isLoading]);

  // Track if we're currently filling the viewport
  const isFillingViewportRef = useRef<boolean>(false);
  
  // Reset fill attempts when filter changes or debouncing starts
  useEffect(() => {
    fillAttemptsRef.current = 0;
  }, [sessionFetchProps.passed_filter, sessionFetchProps.view, isDebouncing]);

  // Auto-fill viewport if needed, with safeguards against infinite loops
  useEffect(() => {
    // Skip if not ready, loading, debouncing, or at attempt limit
    // Also skip if we don't have flattened items (not rendered yet)
    if (!virtualizerReady || isLoadingRef.current || isDebouncing || !hasMoreRef.current || 
        fillAttemptsRef.current >= MAX_FILL_ATTEMPTS || flattenedItems.length === 0 || !rowVirtualizer ||
        isFillingViewportRef.current) {
      return;
    }
    
    const container = _listRef.current;
    if (!container) {
      return;
    }
    
    // Delayed check to ensure layout has settled and items are rendered
    const checkAndLoadMore = () => {
      const contentHeight = rowVirtualizer.getTotalSize();
      const viewportHeight = container.clientHeight;
      
      // Only load more if we need significantly more content
      if (contentHeight < viewportHeight + 50) {
        isFillingViewportRef.current = true;
        fillAttemptsRef.current += 1;
        loadMore(true);
        // Reset the filling flag after a delay
        setTimeout(() => {
          isFillingViewportRef.current = false;
        }, 1000, 'fillViewportReset');
      }
    };
    
    setTimeout(checkAndLoadMore, 500, 'viewportFillCheck');
    return () => clearTimeout('viewportFillCheck');
  }, [
    rowVirtualizer, 
    loadMore, 
    setTimeout, 
    clearTimeout, 
    flattenedItems.length, // Use flattened items length instead of items.length
    virtualizerReady,
    isDebouncing, // Add isDebouncing as dependency
  ]);

  // Function to handle historical data loading with proper loading state
  const handleHistoricalSearch = useCallback(() => {
    fillAttemptsRef.current = 0;
    
    // Just call loadMore - no need to manage loading state here
    // Load more with the historical search parameter (false means not from infinite scroll)
    loadMore(false);
  }, [loadMore]);

  // Function to poll for reviews
  const pollForReviews = useCallback(() => {
    reloadPendingReviewsInitialState(dispatch);
    setTimeout(() => pollForReviews(), 5*MIN_TO_SEC*SEC_TO_MS, 'pollReviews');
  }, [dispatch, setTimeout]);

  // Initial polling setup
  useEffect(() => {
    if (sessionFetchProps.view !== SessionDetailView.MAIN) return
    setTimeout(() => pollForReviews(), 10*SEC_TO_MS, 'initialPollReviews');
  }, [pollForReviews, setTimeout, sessionFetchProps.view]);

  // Create deterministic color maps for disposition and sentiment
  const dispositionColorMap = useMemo(() => {
    return new Map<string, { bg: string, text: string }>();
  }, []);

  const sentimentColorMap = useMemo(() => {
    return new Map<string, { bg: string, text: string }>();
  }, []);

  // Function to get or set color for a disposition/sentiment
  const getTagColors = useCallback((value: string, isDisposition: boolean) => {
    const map = isDisposition ? dispositionColorMap : sentimentColorMap;
    if (!map.has(value)) {
      const stringToColor = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const h = hash % 360;
        return h;
      };

      const hue = stringToColor(value);
      const bg = `hsl(${hue}, 70%, 90%)`;
      const text = `hsl(${hue}, 70%, 30%)`;
      
      map.set(value, { bg, text });
    }
    return map.get(value)!;
  }, [dispositionColorMap, sentimentColorMap]);

  // Add a handler for checkbox clicks
  const handleCheckboxClick = useCallback((e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation(); // Prevent row click when clicking checkbox
    
    if (onSessionSelect) {
      const newSelectedSessions = selectedSessions ? [...selectedSessions] : [];
      const sessionIndex = newSelectedSessions.indexOf(sessionId);
      
      if (sessionIndex === -1) {
        // When selecting (checking the box)
        newSelectedSessions.push(sessionId);
        // Also navigate to this call
        onRowClick(sessionId);
      } else {
        // When deselecting, just remove from selection without navigation
        newSelectedSessions.splice(sessionIndex, 1);
      }
      onSessionSelect(newSelectedSessions);
    }
  }, [onSessionSelect, selectedSessions, onRowClick]);

  // Add a handler for row clicks
  const handleRowClick = useCallback((sessionId: string) => {
    // Only handle navigation, no selection
    onRowClick(sessionId);
  }, [onRowClick]);


  // Add this effect near your other effects
  useEffect(() => {
    const updateTooltipPosition = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.matches('[data-tooltip-text]')) return;
      
      const rect = target.getBoundingClientRect();
      const tooltipX = rect.left;
      const tooltipY = rect.top - 8; // Position above the element with a small gap
      
      document.documentElement.style.setProperty('--tooltip-x', `${tooltipX}px`);
      document.documentElement.style.setProperty('--tooltip-y', `${tooltipY}px`);
    };

    document.addEventListener('mousemove', updateTooltipPosition);
    return () => document.removeEventListener('mousemove', updateTooltipPosition);
  }, []);

  return (
    <div className="h-full min-w-[240px]">
      {flattenedItems.length > 0 ? (
        <div className="h-full bg-white rounded-lg shadow-sm">
          <div className="h-full overflow-y-auto isolate" ref={_listRef}>
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
                willChange: 'transform',
                contain: 'strict',
                isolation: 'isolate',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow, idx) => {
                const item = flattenedItems[virtualRow.index]
                if (!item) return null;
                return (
                  <div
                    key={virtualRow.key as React.Key}
                    data-index={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      willChange: 'transform',
                      contain: 'content',
                      zIndex: virtualRow.index,
                      backgroundColor: item.type === 'header' ? '#F9FAFB' : '#FFFFFF',
                    }}
                  >
                    {item.type === 'header' ? (
                      <div 
                        className="px-3 py-1.5 border-b border-gray-100 w-full"
                      >
                        <h2 className="text-sm font-medium text-gray-900">{item.date}</h2>
                      </div>
                    ) : (
                      <div 
                        className={`group flex h-full border-b border-gray-100 transition-colors cursor-pointer hover:bg-gray-50 ${
                          shownSessionId === item.data.session_id ? 'bg-gray-200' : ''
                        } ${
                          isSessionMissed(item.data) && sessionFetchProps.tab !== SessionHistoryTabs.MISSED ? 'bg-red-50' : ''
                        } ${
                          sessionFetchProps.tab === SessionHistoryTabs.CALLBACKS ? 'py-1' : ''
                        }`}
                        onClick={() => handleRowClick(item.data.session_id)}
                      >
                        {/* Add checkbox if view supports session select */}
                        {viewSupportsSessionSelect(sessionFetchProps.view) && (
                          <div className="flex-shrink-0 px-2 py-2 flex items-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                              checked={selectedSessions?.includes(item.data.session_id)}
                              onChange={(e) => e.stopPropagation()}
                              onClick={(e) => handleCheckboxClick(e, item.data.session_id)}
                            />
                          </div>
                        )}
                        <div className="flex-shrink-0 px-2 py-2">
                          {/* Rep initials bubble */}
                          {(() => {
                            // Try the new smart user ID logic first, fall back to legacy RESOURCE_ID
                            const userId = getSessionUserId(item.data) || item.data.result.get(SessionV2Columns.RESOURCE_ID);
                            const isBot = item.data.result.get(SessionAgentsColumns.AGENT_TYPES_STR)?.includes(AgentType.VOICE_BOT)
                            const repName = visibleAccounts?.users.find((u) => u.user_id === userId)?.user_name ?? (isBot ? "Bot" : "Unknown");
                            const initials = repName.split(' ').map(n => n[0]).join('').toUpperCase();
                            return (
                              <div className="relative inline-block">
                                <div className="peer w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center cursor-pointer">
                                  <span className="text-xs font-medium text-blue-700">{initials}</span>
                                </div>
                                <div className="absolute hidden peer-hover:block left-full top-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-10">
                                  {repName}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex-1 min-w-0 py-2 pr-3">
                          {/* First row: Name + Status/Duration */}
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                              <span className="font-medium text-sm text-gray-900 truncate flex-1 min-w-0">
                                {(() => {
                                  const name = item.data.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_NAME)
                                  const title = item.data.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_TITLE) 
                                  const company = item.data.result.get(PROSPECT_INFO_COLUMNS.COMPANY_NAME)
                                  const number = item.data.customer_address
                                  let displayText = name || number || 'Unknown';
                                  if (name?.toLowerCase().includes('unknown')) { displayText = number || name; }
                                  return (
                                    <div className="flex items-center gap-1.5 min-w-0 w-full">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="font-medium truncate flex-shrink-0 max-w-[180px]">{displayText}</span>
                                        {sessionFetchProps.tab === SessionHistoryTabs.CALLBACKS && (
                                          <span className="inline-flex items-center flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 truncate" title={item.data.result.get(CallbackRequestColumns.CALLBACK_STATUS) || 'Unknown'}>
                                            {item.data.result.get(CallbackRequestColumns.CALLBACK_STATUS) || 'Unknown'}
                                          </span>
                                        )}
                                      </div>
                                      {(title || company) && (
                                        <span className="text-sm text-gray-400 truncate flex-1 min-w-0">
                                          {title}
                                          {title && company && " · "}
                                          {company}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                              {user && (
                                <div className={`${item.data.result.get(ReviewStatusColumns.IS_OPEN) === null ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'} transition-opacity`}>
                                  <SessionStatus 
                                    isMainView={true} 
                                    sessionId={item.data.session_id} 
                                    sessionUserId={getSessionUserId(item.data) || item.data.result.get(SessionV2Columns.RESOURCE_ID) || ''}
                                    type={'sessionList'} 
                                    viewerUserId={user.user_id} 
                                    reviewIsOpen={item.data.result.get(ReviewStatusColumns.IS_OPEN)}
                                    reviewOpenedByUser={item.data.result.get(ReviewStatusColumns.OPENING_USER_ID) === user.user_id}
                                  />
                                </div>
                              )}
                              {(!sessionFetchProps.tab || (sessionFetchProps.tab !== SessionHistoryTabs.CALLBACKS && sessionFetchProps.tab !== SessionHistoryTabs.TEXTS)) && (
                                <span className="text-sm text-gray-400 tabular-nums">
                                  {(() => {
                                    const callDuration = item.data.result.get(CallMetricColumns.CALL_DURATION) ?? 0;
                                    const numericCallDuration = typeof callDuration === 'string' ? parseFloat(callDuration) : callDuration;
                                    return durationToString(numericCallDuration * SEC_TO_MS);
                                  })()}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Second row: Contact status + disposition/sentiment OR Callback reasoning OR Text preview */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 overflow-hidden w-full">
                              <div className="flex items-center flex-nowrap gap-1.5 w-full overflow-hidden">
                                {sessionFetchProps.tab === SessionHistoryTabs.CALLBACKS ? (
                                  <div className="relative inline-flex w-full min-w-0">
                                    <div className="group relative inline-flex w-full min-w-0">
                                      <span className="inline-flex items-center px-2 py-1.5 rounded text-xs font-medium bg-gray-50 text-gray-700 w-full truncate">
                                        {item.data.result.get(CallbackRequestColumns.CALLBACK_REASONING) || 'No reason provided'}
                                      </span>
                                      <div className="absolute z-50 invisible group-hover:visible bottom-full left-0 mb-2 w-64">
                                        <div className="bg-gray-900 text-white text-xs rounded px-2.5 py-1.5 relative">
                                          {item.data.result.get(CallbackRequestColumns.CALLBACK_REASONING) || 'No reason provided'}
                                          <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -bottom-1 left-4"></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : sessionFetchProps.tab === SessionHistoryTabs.TEXTS ? (
                                  <span className="text-sm text-gray-500 truncate w-full">
                                    {(() => {
                                      const lastMessage = getSessionLastMessage(item.data);
                                      const isLastMessageFromAgent = isSessionLastMessageFromAgent(item.data);
                                      return `${isLastMessageFromAgent ? 'Agent' : 'Prospect'}: ${lastMessage}`;
                                    })()}
                                  </span>
                                ) : (
                                  <>
                                    {(() => {
                                      // Get disposition and sentiment
                                      const disposition = getSessionDisposition(item.data);
                                      const sentiment = getSessionSentiment(item.data);
                                      const isAnswered = isSessionAnswered(item.data);
                                      const objectionString = getSessionObjectionString(item.data);
                                      
                                      // Get custom scores for this session
                                      const customScores = getCustomScoresForSession(item.data.session_id);
                                      
                                      // Show custom scores alongside disposition/sentiment (only in main/analytics views)
                                      const hasCustomScores = customScores && customScores.hasCustomScores && viewSupportsCustomScores(sessionFetchProps.view);
                                      
                                      // If we're showing a session detail (compressed view), show custom metrics as +X but keep all other tags normal
                                      const isCompressedView = shownSessionId !== undefined;
                                      
                                      // Calculate smart max-width based on number of tags in compressed view
                                      const getSmartMaxWidth = (tagCount: number) => {
                                        if (!isCompressedView) return undefined;
                                        
                                        // Approximate container width is ~400px, with gaps of 6px each
                                        // Reserve space for padding, gaps, and ensure it doesn't wrap
                                        const containerWidth = 350; // Conservative estimate
                                        const gapWidth = 6;
                                        const availableWidth = containerWidth - (gapWidth * (tagCount - 1));
                                        const maxWidthPerTag = Math.floor(availableWidth / tagCount);
                                        
                                        // Set reasonable bounds
                                        return `${Math.max(60, Math.min(120, maxWidthPerTag))}px`;
                                      };
                                      
                                      // Count potential tags to determine max-width
                                      let tagCount = 0;
                                      if (disposition) tagCount++;
                                      if (sentiment) tagCount++;
                                      if (objectionString && isAnswered) tagCount++;
                                      if (hasCustomScores) {
                                        if (isCompressedView) {
                                          if (disposition || sentiment) {
                                            tagCount++; // +X tag
                                          } else {
                                            tagCount++; // 1 custom score
                                            if (customScores.customScoresCount > 1) tagCount++; // +X more tag
                                          }
                                        } else {
                                          tagCount += Math.min(3, customScores.customScoresCount);
                                          if (customScores.customScoresCount > 3) tagCount++; // +X more tag
                                        }
                                      }
                                      if (!hasCustomScores && !disposition && !sentiment) {
                                        // Default tags
                                        if (!isAnswered && !isSessionMissed(item.data)) tagCount++; // no contact
                                        if (isSessionMissed(item.data)) tagCount++; // missed
                                        if (isAnswered) {
                                          const targetDuration = item.data.result.get(SESSION_METRICS_COLUMNS.TARGET_DURATION) ?? 0;
                                          const gatekeeperDuration = item.data.result.get(SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION) ?? 0;
                                          const numericTargetDuration = typeof targetDuration === 'string' ? parseFloat(targetDuration) : targetDuration;
                                          const numericGatekeeperDuration = typeof gatekeeperDuration === 'string' ? parseFloat(gatekeeperDuration) : gatekeeperDuration;
                                          
                                          if ((numericTargetDuration > 0 || numericGatekeeperDuration > 0) || (numericTargetDuration === 0 && numericGatekeeperDuration === 0)) {
                                            tagCount++; // prospect/gatekeeper/live person
                                          }
                                        }
                                      }
                                      
                                      const smartMaxWidth = getSmartMaxWidth(tagCount);

                                      return (
                                        <div className="inline-flex flex-nowrap gap-1.5 overflow-hidden">
                                          {/* Disposition - show first */}
                                          {disposition && (
                                            <span 
                                              className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium truncate"
                                              style={{
                                                backgroundColor: getTagColors(disposition, true).bg,
                                                color: getTagColors(disposition, true).text,
                                                ...(isCompressedView && { maxWidth: smartMaxWidth })
                                              }}
                                            >
                                              {disposition}
                                            </span>
                                          )}
                                          
                                          {/* Sentiment - show second */}
                                          {sentiment && (
                                            <span 
                                              className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium truncate"
                                              style={{
                                                backgroundColor: getTagColors(sentiment, false).bg,
                                                color: getTagColors(sentiment, false).text,
                                                ...(isCompressedView && { maxWidth: smartMaxWidth })
                                              }}
                                            >
                                              {sentiment}
                                            </span>
                                          )}
                                          
                                          {/* Custom Scores - show differently in compressed vs normal view */}
                                          {hasCustomScores && (
                                            isCompressedView ? (
                                              // Compressed view logic
                                              (disposition || sentiment) ? (
                                                // Has disposition/sentiment: show +X for all custom metrics
                                                customScores.customScoresCount > 0 && (
                                                  <span 
                                                    className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium truncate cursor-pointer"
                                                    style={{
                                                      backgroundColor: '#f3f4f6',
                                                      color: '#6b7280',
                                                      maxWidth: smartMaxWidth
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const rect = e.currentTarget.getBoundingClientRect();
                                                      showModal(item.data.session_id, rect);
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      const rect = e.currentTarget.getBoundingClientRect();
                                                      showModal(item.data.session_id, rect);
                                                    }}
                                                    onMouseLeave={hideModal}
                                                  >
                                                    +{customScores.customScoresCount}
                                                  </span>
                                                )
                                              ) : (
                                                // No disposition/sentiment: show 1 custom metric + +X more if others
                                                <>
                                                  {customScores.scoresSummary.slice(0, 1).map((score, index) => (
                                                    <span 
                                                      key={`custom-${index}`}
                                                      className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium truncate cursor-help"
                                                      style={{
                                                        backgroundColor: score.backgroundColor,
                                                        color: score.textColor,
                                                        maxWidth: smartMaxWidth
                                                      }}
                                                      title={`${score.label}: ${score.displayValue}`}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        showModal(item.data.session_id, rect);
                                                      }}
                                                      onMouseEnter={(e) => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        showModal(item.data.session_id, rect);
                                                      }}
                                                      onMouseLeave={hideModal}
                                                    >
                                                      {score.label}: {score.displayValue}
                                                    </span>
                                                  ))}
                                                  
                                                  {/* Show +X more if there are more than 1 custom scores */}
                                                  {customScores.customScoresCount > 1 && (
                                                    <span 
                                                      className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium truncate cursor-pointer"
                                                      style={{
                                                        backgroundColor: '#f3f4f6',
                                                        color: '#6b7280',
                                                        maxWidth: smartMaxWidth
                                                      }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        showModal(item.data.session_id, rect);
                                                      }}
                                                      onMouseEnter={(e) => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        showModal(item.data.session_id, rect);
                                                      }}
                                                      onMouseLeave={hideModal}
                                                    >
                                                      +{customScores.customScoresCount - 1} more
                                                    </span>
                                                  )}
                                                </>
                                              )
                                            ) : (
                                              // Normal view: show up to 3 custom metrics (no max-width) + +X more
                                              <>
                                                {customScores.scoresSummary.slice(0, 3).map((score, index) => (
                                                  <span 
                                                    key={`custom-${index}`}
                                                    className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium truncate cursor-help"
                                                    style={{
                                                      backgroundColor: score.backgroundColor,
                                                      color: score.textColor
                                                    }}
                                                    title={`${score.label}: ${score.displayValue}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const rect = e.currentTarget.getBoundingClientRect();
                                                      showModal(item.data.session_id, rect);
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      const rect = e.currentTarget.getBoundingClientRect();
                                                      showModal(item.data.session_id, rect);
                                                    }}
                                                    onMouseLeave={hideModal}
                                                  >
                                                    {score.label}: {score.displayValue}
                                                  </span>
                                                ))}
                                                
                                                {/* Show +X more if there are more than 3 custom scores */}
                                                {customScores.customScoresCount > 3 && (
                                                  <span 
                                                    className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium truncate cursor-pointer"
                                                    style={{
                                                      backgroundColor: '#f3f4f6',
                                                      color: '#6b7280'
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const rect = e.currentTarget.getBoundingClientRect();
                                                      showModal(item.data.session_id, rect);
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      const rect = e.currentTarget.getBoundingClientRect();
                                                      showModal(item.data.session_id, rect);
                                                    }}
                                                    onMouseLeave={hideModal}
                                                  >
                                                    +{customScores.customScoresCount - 3} more
                                                  </span>
                                                )}
                                              </>
                                            )
                                          )}
                                          
                                          {/* Default tags (only show if no custom scores and no disposition/sentiment) */}
                                          {!hasCustomScores && !disposition && !sentiment && (
                                            <>
                                              {!isAnswered && !isSessionMissed(item.data) && (
                                                <span 
                                                  className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-400 truncate"
                                                  style={{...(isCompressedView && { maxWidth: '80px' })}}
                                                >
                                                  no contact
                                                </span>
                                              )}
                                              {isSessionMissed(item.data) && (
                                                <span 
                                                  className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 truncate"
                                                  style={{...(isCompressedView && { maxWidth: '80px' })}}
                                                >
                                                  missed
                                                </span>
                                              )}
                                              {isAnswered && (
                                                <>
                                                  {(() => {
                                                    const targetDuration = item.data.result.get(SESSION_METRICS_COLUMNS.TARGET_DURATION) ?? 0;
                                                    const gatekeeperDuration = item.data.result.get(SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION) ?? 0;
                                                    const numericTargetDuration = typeof targetDuration === 'string' ? parseFloat(targetDuration) : targetDuration;
                                                    const numericGatekeeperDuration = typeof gatekeeperDuration === 'string' ? parseFloat(gatekeeperDuration) : gatekeeperDuration;
                                                    
                                                    return (
                                                      <>
                                                        {(numericTargetDuration > 0) && (
                                                          <span 
                                                            className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 truncate"
                                                            style={{...(isCompressedView && { maxWidth: '80px' })}}
                                                          >
                                                            prospect
                                                          </span>
                                                        )}
                                                        {(numericTargetDuration === 0) && (numericGatekeeperDuration > 0) && (
                                                          <span 
                                                            className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 truncate"
                                                            style={{...(isCompressedView && { maxWidth: '80px' })}}
                                                          >
                                                            gatekeeper
                                                          </span>
                                                        )}
                                                        {(numericTargetDuration === 0) && (numericGatekeeperDuration === 0) && (
                                                          <span 
                                                            className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 truncate"
                                                            style={{...(isCompressedView && { maxWidth: '80px' })}}
                                                          >
                                                            live person
                                                          </span>
                                                        )}
                                                      </>
                                                    );
                                                  })()}
                                                </>
                                              )}
                                            </>
                                          )}
                                          
                                          {/* Always show objection tags if there's a connect and objections */}
                                          {isAnswered && objectionString && (
                                            <span 
                                              className="inline-flex items-center flex-shrink min-w-0 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 truncate" 
                                              title={objectionString}
                                              style={{...(isCompressedView && { maxWidth: smartMaxWidth })}}
                                            >
                                              {objectionString}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Loading spinner and load more button - with controlled visibility */}
            {(isLoading || hasMore || isDebouncing) && (
              <div className="flex items-center justify-center py-6 mt-4">
                {(isLoading || isDebouncing) ? (
                  <div className="flex items-center space-x-3 px-5 py-3 bg-blue-50 rounded-lg shadow-sm">
                    <div className="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                    <span className="text-sm font-medium text-blue-700">
                      {isDebouncing ? "Updating filters..." : "Loading more sessions..."}
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      fillAttemptsRef.current = 0;
                      loadMore(true);
                    }}
                    className="px-6 py-3 bg-blue-100 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    disabled={isLoading || isLoadingRef.current || isDebouncing}
                  >
                    Load more sessions
                  </button>
                )}
              </div>
            )}
            
            {/* End message with better spacing */}
            {!isLoading && !hasMore && !isDebouncing && items.length > 0 && (
              <div className="flex items-center justify-center py-6">
                {monthsSearched < ABSOLUTE_MAX_LOOKBACK_MONTHS && !viewEnforcesStart(sessionFetchProps.view) && !hasStrictStartDate ? (
                  <button
                    onClick={() => {
                      handleHistoricalSearch();
                    }}
                    className="px-6 py-3 bg-blue-100 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    disabled={isLoading || isLoadingRef.current || isDebouncing}
                  >
                    Look beyond {monthsSearched} {monthsSearched === 1 ? 'month' : 'months'}
                  </button>
                ) : (
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500 block">
                      {monthsSearched >= ABSOLUTE_MAX_LOOKBACK_MONTHS 
                        ? `No more sessions found beyond 1 year (searched ${monthsSearched} months)` 
                        : `No more sessions found in the past ${monthsSearched} ${monthsSearched === 1 ? 'month' : 'months'}`}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 space-y-4">
          {isLoading || isLoadingRef.current || isDebouncing ? (
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
              <span>{isDebouncing ? "Updating filters..." : "Loading sessions..."}</span>
            </div>
          ) : (
            <>
              <div className="text-center space-y-2">
                <span className="text-sm block">
                  {monthsSearched >= ABSOLUTE_MAX_LOOKBACK_MONTHS 
                    ? `No sessions found beyond 1 year (searched ${monthsSearched} months)` 
                    : `No sessions found in the past ${monthsSearched} ${monthsSearched === 1 ? 'month' : 'months'}`}
                </span>
                <span className="text-xs text-gray-400 block">
                  {monthsSearched >= ABSOLUTE_MAX_LOOKBACK_MONTHS || hasStrictStartDate
                    ? "You've reached the maximum lookback period" 
                    : "Would you like to search further back?"}
                </span>
              </div>
              {monthsSearched < ABSOLUTE_MAX_LOOKBACK_MONTHS && !hasStrictStartDate && (
                <button
                  onClick={handleHistoricalSearch}
                  className="px-6 py-3 bg-blue-100 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  disabled={isLoading || isLoadingRef.current || isDebouncing}
                >
                  Look back further
                </button>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Custom Scores Modal */}
      {modalSessionId && items.find(item => item.session_id === modalSessionId) && (
        <CustomScoresModal
          session={items.find(item => item.session_id === modalSessionId)!}
          isOpen={isCustomScoresModalOpen}
          onClose={closeModal}
          anchorRect={modalAnchorRect}
          position="auto"
          onMouseEnter={cancelHideModal}
          onMouseLeave={startHideModal}
        />
      )}
    </div>
  );
}